import { Info } from 'lucide-react';
import type { AccountMetricsResponse } from '@pnl/shared';
import { formatBrl, formatNumber, formatPct } from '../../lib/format';
import { Tooltip } from '../Tooltip';
import { Card } from './Card';

interface Props {
  stats: AccountMetricsResponse['statistics'];
}

type Tone = 'pos' | 'neg' | 'neutral';

interface Cell {
  label: string;
  value: string;
  tone: Tone;
  tooltip?: string;
}

function StatCell({ label, value, tone, tooltip }: Cell) {
  const color =
    tone === 'pos' ? 'text-success-300' : tone === 'neg' ? 'text-danger-300' : 'text-ink-100';
  return (
    <div className="border-ink-800 bg-ink-950 rounded-md border px-3 py-2.5 text-center">
      <p className="text-2xs flex items-center justify-center gap-1 font-medium text-[#B5B5B5]">
        <span className="truncate">{label}</span>
        {tooltip ? (
          <Tooltip label={tooltip}>
            <Info size={10} className="text-ink-500 shrink-0" />
          </Tooltip>
        ) : (
          <Info size={10} className="text-ink-500 shrink-0" />
        )}
      </p>
      <p className={'mt-0.5 text-sm font-semibold tabular-nums ' + color}>{value}</p>
    </div>
  );
}

export function Statistics({ stats }: Props) {
  const primary: Cell[] = [
    {
      label: 'Equidade',
      value: formatBrl(stats.equityCents),
      tone: 'neutral',
      tooltip: 'Valor da conta em tempo real: Saldo + P/L flutuante de posições em aberto.',
    },
    {
      label: 'Balance',
      value: formatBrl(stats.balanceCents),
      tone: 'neutral',
      tooltip:
        'O saldo representa o depósito inicial e quaisquer lucros ou perdas de posições encerradas.',
    },
  ];

  const grid: Cell[] = [
    {
      label: 'Taxa de acerto',
      value: formatPct(stats.winRatePct, 2),
      tone: 'pos',
      tooltip: 'Porcentagem de posições encerradas que resultaram em lucro.',
    },
    {
      label: 'Média de lucros',
      value: formatBrl(stats.avgWinCents),
      tone: 'pos',
      tooltip: 'Ganho monetário médio das posições vencedoras.',
    },
    {
      label: 'Prejuízo médio',
      value: formatBrl(stats.avgLossCents),
      tone: 'neg',
      tooltip: 'Perda monetária média resultante de posições com prejuízo.',
    },
    {
      label: 'Número de Trades',
      value: String(stats.numberOfTrades),
      tone: 'neutral',
      tooltip: 'Total de posições encerradas.',
    },
    {
      label: 'RR Médio',
      value: formatNumber(stats.avgRRR, 2),
      tone: 'neutral',
      tooltip:
        'Relação média entre recompensa e risco — mostra quanto normalmente ganha numa posição vencedora em comparação com quanto perde numa posição perdedora.',
    },
    {
      label: 'Fator de lucro',
      value: formatNumber(stats.profitFactor, 2),
      tone: 'neutral',
      tooltip:
        'Lucros brutos ÷ perdas brutas; valores acima de 1 significam que a estratégia é lucrativa.',
    },
  ];

  return (
    <Card title="Estatísticas">
      <div className="space-y-2 p-4">
        <div className="grid grid-cols-2 gap-2">
          {primary.map((c) => (
            <StatCell key={c.label} {...c} />
          ))}
        </div>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {grid.map((c) => (
            <StatCell key={c.label} {...c} />
          ))}
        </div>
      </div>
    </Card>
  );
}
